/**
 * The custom text rules of an ABCDior order: the pattern the text must match
 * depends on the product line keyboard and its options.
 */
import type { Order } from './api';

export const KEYBOARD_REGEXES: Readonly<Record<string, string>> = {
  latin: '[a-zA-Z ]|<[a-z]+>',
  chinese: String.raw`[\p{Script=Hani} ]`,
  chineseTw: String.raw`[\p{Script=Hani} ]`,
  chineseHk: String.raw`[\p{Script=Hani} ]`,
  arabic: String.raw`[\u0600-\u06FF ]`,
  cyrillic: String.raw`(?![ЁЙёй])[\p{Script=Cyrillic} ]`,
  number: String.raw`\d`,
  dot: String.raw`\.`,
  dash: '-',
};

export interface KeyboardSpecialChars {
  numbers?: unknown;
  dot?: unknown;
  dash?: unknown;
  oneSpaceBetweenChars?: unknown;
}

/** The product line fields the details dialog reads (from the app config). */
export interface OrderProductLine {
  productLine?: string | null | undefined;
  sizeDisplay?: string | null | undefined;
  keyboard?:
    | (Record<string, unknown> & {
        specialChars?: KeyboardSpecialChars | null | undefined;
      })
    | null
    | undefined;
}

/** The source of one character run, unanchored. */
export const keyboardRegexSource = (
  keyboard: string,
  specialChars: KeyboardSpecialChars | null | undefined,
  maxChars = -1,
): string => {
  const options = specialChars ?? {};
  const letter = KEYBOARD_REGEXES[keyboard] ?? '.';
  const noDoubleSpace = options.oneSpaceBetweenChars
    ? String.raw`(?!.*\s{2})`
    : '';
  let alternatives = `(${letter})`;
  if (options.numbers) alternatives += `|(${KEYBOARD_REGEXES.number})`;
  if (options.dot) alternatives += `|(${KEYBOARD_REGEXES.dot})`;
  if (options.dash) alternatives += `|(${KEYBOARD_REGEXES.dash})`;
  const quantifier = maxChars === -1 ? '*' : `{0,${maxChars}}`;
  return `${noDoubleSpace}(${alternatives})${quantifier}`;
};

const DEFAULT_MAX_CHARS = 13;

const keyboardMaxChars = (
  line: OrderProductLine | null | undefined,
  alphabet: string,
): number | null => {
  const settings = line?.keyboard?.[alphabet];
  if (!settings || typeof settings !== 'object') return null;
  const value = Number((settings as { maxChars?: unknown }).maxChars);
  return value > 0 ? value : null;
};

/** The keyboard limit, doubled plus the comma for a pair of shoes. */
export const maxCharsFor = (
  order: Pick<Order, 'alphabet'>,
  line: OrderProductLine | null | undefined,
): number => {
  const maxChars = keyboardMaxChars(line, String(order.alphabet ?? 'latin'));
  if (!maxChars || line?.sizeDisplay !== 'shoeSize') {
    return maxChars ?? DEFAULT_MAX_CHARS;
  }
  return maxChars * 2 + 1;
};

/** The pattern of the custom text input, one side per shoe. */
export const customTextPattern = (
  order: Pick<Order, 'alphabet'>,
  line: OrderProductLine | null | undefined,
): RegExp => {
  const keyboard = String(order.alphabet ?? 'latin');
  const maxChars = maxCharsFor(order, line);
  const specialChars = line?.keyboard?.specialChars;
  if (line?.sizeDisplay === 'shoeSize') {
    const oneSide = keyboardRegexSource(
      keyboard,
      specialChars,
      (maxChars - 1) / 2,
    );
    return new RegExp(`^${oneSide},${oneSide}$`, 'u');
  }
  return new RegExp(
    `^${keyboardRegexSource(keyboard, specialChars, maxChars)}$`,
    'u',
  );
};

const SYMBOL_TAGS = /<[a-z]+>/g;

/** A symbol tag counts for two characters. */
export const customTextLength = (text: string | null | undefined): number => {
  const value = text ?? '';
  const symbols = value.match(SYMBOL_TAGS)?.length ?? 0;
  return value.replace(SYMBOL_TAGS, '').length + symbols * 2;
};
